import React from "react";
import styled from "styled-components";
import { SpaceCreatorLinkType, VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import MoreActionsMenu, { ActionDataModel } from "@common/MoreActionsMenu/MoreActionsMenu";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import InstagramIcon from "@resources/logos/social/Instagram.svg";
import TikTokIcon from "@resources/logos/social/TikTok.svg";
import YouTubeIcon from "@resources/logos/social/Youtube.svg";
import { BrowserUtil } from "@util/index";
import "./SpaceCreatorInfo.scss";
import LinkIcon from "/resources/icons/LinkIcon.svg";
import TipIcon from "/resources/icons/TipJarIcon.svg";
import VerifiedIcon from "/resources/icons/VerifiedCheck.svg";
import DefaultCreatorAvi from "/resources/images/Creator-Avi-Placeholder.svg";
import SpotifyIcon from "/resources/logos/social/Spotify.svg";

const REPORT_SPACE_LABEL = "Report space";
const REPORT_SPACE_LINK = "https://airtable.com/shrzBczpSUnFIiXWi";
const REPORT_CREATOR_LABEL = "Report creator";
const REPORT_CREATOR_LINK = "https://airtable.com/shrMWF1uTsaT1Qncj";

interface SpaceCreatorInfoProps {
    /**
     * The creator's username handle.
     */
    readonly creatorUsername: VirtualSpace["creatorName"];
    /**
     * The URL to the location of the creator's avi in Google Cloud Storage.
     * If not provided, the default avi (multicolored circle) will display.
     */
    readonly creatorAviUrl?: VirtualSpace["creatorAviUrl"];
    /**
     * Links to the creator's social profiles.
     */
    readonly creatorSocialLinks?: VirtualSpace["creatorSocialLinks"];
    readonly showCreatorNameOnly?: boolean;
    className?: string;
}
export default function SpaceCreatorInfo(props: SpaceCreatorInfoProps) {
    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const verifiedIcon =
        props.creatorUsername !== undefined ? <VerifiedIcon className={`space-creator-verified-icon`} /> : null;

    const actions: ActionDataModel[] = [
        { name: REPORT_SPACE_LABEL, actionHandler: () => BrowserUtil.openLinkNewTab(REPORT_SPACE_LINK) },
        { name: REPORT_CREATOR_LABEL, actionHandler: () => BrowserUtil.openLinkNewTab(REPORT_CREATOR_LINK) },
    ];

    const creatorLinksComponents = props.creatorSocialLinks
        ? Object.entries(props.creatorSocialLinks).map(([socialKey, socialUrl]: [SpaceCreatorLinkType, string]) => {
              return (
                  socialKey &&
                  socialUrl && (
                      <LifeAtButton
                          key={`creator-social-link-button-${socialUrl}`}
                          isLink={true}
                          link={socialUrl}
                          icon={getCreatorSocialLinkIconForKey(socialKey, lightMode)}
                      />
                  )
              );
          })
        : null;

    return (
        <div className={`space-creator-info ${lightMode ? "light" : "dark"} ${props.className}`}>
            {!props.showCreatorNameOnly && <SpaceCreatorAvi aviUrl={props.creatorAviUrl} />}

            <span className={`creator-text-content`}>
                <div className={`creator-username ${lightMode ? "light" : "dark"}`}>
                    {buildFormattedUserName(props.creatorUsername)}
                    {verifiedIcon}
                </div>

                {!props.showCreatorNameOnly && creatorLinksComponents && (
                    <div className={`creator-links`}>
                        {creatorLinksComponents}
                        <span>
                            <MoreActionsMenu actions={actions} />
                        </span>
                    </div>
                )}
            </span>
        </div>
    );
}

const buildFormattedUserName = (userName: string): string => {
    return `@${userName}`;
};

export const getCreatorSocialLinkIconForKey = (
    socialKey: SpaceCreatorLinkType,
    lightMode: boolean,
): JSX.Element | undefined => {
    let icon;
    switch (socialKey) {
        case SpaceCreatorLinkType.YOUTUBE: {
            icon = <YouTubeIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        case SpaceCreatorLinkType.INSTAGRAM: {
            icon = <InstagramIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        case SpaceCreatorLinkType.SPOTIFY: {
            icon = <SpotifyIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        case SpaceCreatorLinkType.TIKTOK: {
            icon = <TikTokIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        case SpaceCreatorLinkType.OTHER: {
            icon = <LinkIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        case SpaceCreatorLinkType.TIPS: {
            icon = <TipIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
        default: {
            icon = <LinkIcon className={`creator-social-link-icon ${lightMode ? "light" : "dark"}`} />;
            break;
        }
    }
    return icon;
};

const AviImg = styled.img`
    height: 44px;
    width: 44px;
    border-radius: 50%;
    margin-right: 12px;
`;

interface SpaceCreatorAviProps {
    readonly aviUrl: string;
}
function SpaceCreatorAvi(props: SpaceCreatorAviProps) {
    return props.aviUrl && props.aviUrl !== "" ? <AviImg src={props.aviUrl} /> : <DefaultCreatorAvi />;
}
